import { useEffect, useState } from 'react'
import { useCallbackRef } from '@buffer-mono/popcorn'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'
import type { CommentsScoreResponse } from '~publish/gql/graphql'
import type { ApolloError } from '@apollo/client'
import { useCommentsScoreQuery } from './useCommentsScoreQuery'

type ApolloScoreState = {
  score: CommentsScoreResponse | null
  loading: boolean
  error: ApolloError | Error | null
}

type UseCommentsScoreResult = {
  commentsScore: CommentsScoreResponse | null
  loading: boolean
  error: ApolloError | Error | null
}

/* comment events that should trigger a score update */
const commentsEvents = [
  PusherEvent.COMMENT_DISMISSED,
  PusherEvent.COMMENT_REPLIED,
  PusherEvent.COMMENT_UNDISMISSED,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_POST,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_CHANNEL,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_ORGANIZATION,
  PusherEvent.COMMENT_INGESTED,
]

/**
 * Hook to manage comments score state and real-time updates
 * @param channelId - The channelId to fetch comments score for
 * @returns Object containing:
 *  - commentsScore: The comments score data
 *  - loading: Loading state
 *  - error: Any error that occurred
 */
export function useCommentsScore(args: {
  channelId: string
}): UseCommentsScoreResult {
  const organizationId = useOrganizationId()
  const { fetchCommentsScore } = useCommentsScoreQuery()

  const [state, setState] = useState<ApolloScoreState>({
    score: null,
    loading: false,
    error: null,
  })

  const fetchAndUpdateScore = useCallbackRef(async () => {
    if (!organizationId) {
      setState({
        score: null,
        loading: false,
        error: new Error('Organization ID is required'),
      })
      return
    }

    setState((prev) => ({ ...prev, loading: true }))

    try {
      const result = await fetchCommentsScore({
        organizationId,
        channelId: args.channelId,
      })

      setState({
        score: result.data?.commentsScore ?? null,
        loading: false,
        error: result.error || null,
      })
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as ApolloError,
      }))
    }
  })

  // Handle null organizationId
  useEffect(() => {
    if (!organizationId) {
      setState({
        score: null,
        loading: false,
        error: new Error('Organization Id is required'),
      })
    }
  }, [organizationId])

  // Initial fetch
  useEffect(() => {
    if (organizationId) {
      fetchAndUpdateScore()
    }
  }, [organizationId, args.channelId, fetchAndUpdateScore])

  // Subscribe to Pusher events
  usePusherEvent(commentsEvents, ({ channelId }: { channelId?: string }) => {
    if (channelId === args.channelId) {
      fetchAndUpdateScore()
    }
  })

  return {
    commentsScore: state.score,
    loading: state.loading,
    error: state.error,
  }
}
